// ChannelPlanAlignmentDrilldown.jsx
import React, { useMemo, useState, useCallback } from "react";
import { LicenseManager } from "ag-charts-enterprise";
import "ag-charts-community";
import "ag-charts-enterprise";
import { AgCharts } from "ag-charts-react";

import { AGGRID_LICENCE_KEY } from "../../../../../shared";
LicenseManager.setLicenseKey(AGGRID_LICENCE_KEY);

const asNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const mapChannel = (ch = {}) => {
    const parentPlanned = asNumber(ch.channel_plannedctc || 0);
    const parentOrdered = asNumber(
        ch.channel_total_budget ?? ch.budget ?? ch.channel_total_budget_in_pmds ?? 0
    );
    const parentOrderedPmds = asNumber(ch.channel_total_budget_in_pmds ?? parentOrdered ?? 0);

    const publishers = ch.nestedData || ch.publishers || [];
    const children = (publishers || []).map((p, idx) => {
        const childOrdered = asNumber(p.publisher_budget ?? p.budget ?? p.publisher_budget_in_pmds ?? 0);
        const childPmds = asNumber(p.publisher_budget_in_pmds ?? 0);

        let childPlanned = 0;
        if (parentOrderedPmds > 0 && childPmds > 0 && parentPlanned > 0) {
            childPlanned = parentPlanned * (childPmds / parentOrderedPmds);
        } else if (p.publisher_plannedctc) {
            childPlanned = asNumber(p.publisher_plannedctc);
        } else {
            childPlanned = childOrdered;
        }

        return {
            name: (p.publisher_display_name || p.publisher_name || p.displayName || p.name || `child-${idx}`),
            planned: Number(childPlanned),
            ordered: Number(childOrdered),
            __raw: p,
        };
    });

    return {
        name: (ch.channel_display_name || ch.channel_name || ch.name || "channel") + ' ❯',
        planned: Number(parentPlanned),
        ordered: Number(parentOrdered),
        children,
        __raw: ch,
    };
};

const ChannelPlanAlignmentDrilldown = ({ channelsData = [] }) => {
    const rawList = useMemo(() => {
        if (!channelsData) return [];
        if (Array.isArray(channelsData)) return channelsData;
        if (channelsData.channelsData && Array.isArray(channelsData.channelsData)) return channelsData.channelsData;
        return [];
    }, [channelsData]);

    const transformedTop = useMemo(() => {
        return rawList.map((ch) => mapChannel(ch));
    }, [rawList]);

    const [stack, setStack] = useState([]);

    const currentView = stack.length === 0 ? { title: "Channels", data: transformedTop } : stack[stack.length - 1];

    const handleBack = useCallback(() => {
        setStack((prev) => {
            if (!prev || prev.length === 0) return prev;
            return prev.slice(0, -1);
        });
    }, []);

    const onSeriesNodeClick = useCallback(
        (evt) => {
            const datum = evt?.detail?.datum ?? evt?.datum ?? null;
            if (!datum) return;

            if (Array.isArray(datum.children) && datum.children.length > 0) {
                setStack((prev) => {
                    const newView = {
                        title: datum.name,
                        data: datum.children,
                        rawParent: datum.__raw ?? null,
                    };
                    return prev.concat([newView]);
                });
            } else {
                handleBack();
            }
        },
        [setStack]
    );

    const options = useMemo(() => {
        const data = currentView?.data || [];
        return {
            legend: { position: "right" },
            series: [
                {
                    type: "bar",
                    xKey: "name",
                    yKey: "planned",
                    yName: "Planned",
                    grouped: true,
                    direction: 'horizontal',
                    fill: "#EBF1F6",
                    stroke: "#EBF1F6",
                    cornerRadius: 5,
                    label: {
                        enabled: false
                    },
                    crisp: true,
                },
                {
                    type: "bar",
                    xKey: "name",
                    yKey: "ordered",
                    yName: "Ordered",
                    grouped: true,
                    direction: 'horizontal',
                    fill: "#21EB7F",
                    stroke: "#21EB7F",
                    cornerRadius: 5,
                    label: {
                        enabled: false
                    },
                    crisp: true,
                },
            ],
            data,
            padding: { top: 12, right: 24, bottom: 36, left: 48 },
            navigator: { enabled: false },
            listeners: {
                seriesNodeClick: onSeriesNodeClick,
            },
            axes: [
                {
                    type: "category",
                    position: "left",
                    line: {
                        enabled: true
                    },
                    gridLine: {
                        style: [{ stroke: 'transparent' }]
                    },
                },
                {
                    type: "number",
                    position: "bottom",
                    line: {
                        enabled: true
                    },
                    label: {
                        enabled: false,
                        formatter: (params) => console.log({params}) || formatCurrency(params.value),
                    },
                    gridLine: {
                        style: [{ stroke: 'transparent' }]
                    },
                },
            ],
        };
    }, [currentView, onSeriesNodeClick]);

    return (
        <AgCharts options={options} className="channel-plan-alignment-drilldown-chart" />
    );
};

export default ChannelPlanAlignmentDrilldown;